import {
  MDType,
  OutputFormat,
  StationApiAxiosParamCreator,
  GridApiAxiosParamCreator,
  TimeseriesApiAxiosParamCreator,
} from '@/api/out';
import ConfigStore from '@/stores/config/app-config-module';
import DatasetStore from '@/stores/dataset/dataset-module';
import ParameterStore from '@/stores/parameter/parameter-module';
import GridStore from '@/stores/grid/grid-module';
import TimeseriesStore from '@/stores/timeseries/timeseries-module';
import StationStore from '@/stores/station/station-module';
import { formatDateForQuery } from '@/util/datetime';

// TODO: There is alot of code repetition in this file. After making a working version, we should definitly refactor
// to make this file DRY

class DownloadService {
  async getStationParameterMetadataDownloadURL(
    fileName: string
  ): Promise<string> {
    const fileBasename = fileName.split('.')[0];
    const resId = DatasetStore.metadata?.datasetParams.resourceId;
    if (!resId) {
      throw Error('Resource id is required for downloading parameter metadata');
    }

    const paramCreator = StationApiAxiosParamCreator();
    const pathWithArgs =
      await paramCreator.historicalStationMetadataCSVStationHistoricalResourceIdMetadataMdtypeGet(
        MDType.Parameters,
        resId,
        fileBasename
      );
    return ConfigStore.dataApiUrl + pathWithArgs.url;
  }

  async getGridParameterMetadataDownloadURL(fileName: string): Promise<string> {
    const fileBasename = fileName.split('.')[0];
    const resId = DatasetStore.metadata?.datasetParams.resourceId;
    if (!resId) {
      throw Error('Resource id is required for downloading parameter metadata');
    }

    const paramCreator = GridApiAxiosParamCreator();
    const pathWithArgs =
      await paramCreator.historicalGridParameterMetadataCSVGridHistoricalResourceIdMetadataParametersGet(
        resId,
        fileBasename
      );
    return ConfigStore.dataApiUrl + pathWithArgs.url;
  }

  async getStationStationMetadataDownloadURL(
    fileName: string
  ): Promise<string> {
    const fileBasename = fileName.split('.')[0];
    const resId = DatasetStore.metadata?.datasetParams.resourceId;
    if (!resId) {
      throw Error('Resource id is required for downloading station metadata');
    }

    const paramCreator = StationApiAxiosParamCreator();
    const pathWithArgs =
      await paramCreator.historicalStationMetadataCSVStationHistoricalResourceIdMetadataMdtypeGet(
        MDType.Stations,
        resId,
        fileBasename
      );
    return ConfigStore.dataApiUrl + pathWithArgs.url;
  }

  async getStationDatasetDownloadURL(obj: {
    fileName: string;
    downloadRange: [Date, Date];
  }): Promise<string> {
    const fileBasename = obj.fileName.split('.')[0];
    const fileExtension = obj.fileName.split('.')[1];
    let fileFormat: OutputFormat.Geojson | OutputFormat.Csv | undefined;
    if (fileExtension === 'json') {
      fileFormat = OutputFormat.Geojson;
    } else if (fileExtension === 'csv') {
      fileFormat = OutputFormat.Csv;
    }
    const params = ParameterStore.selectedParameters.map(
      (option) => option.name
    );
    const resId = DatasetStore.metadata?.datasetParams.resourceId;
    let startDate;
    let endDate;
    const dateRange = obj.downloadRange;
    if (dateRange !== undefined) {
      startDate = dateRange[0];
      endDate = dateRange[1];
    } else {
      // fallback to full range available in dataset
      startDate = DatasetStore.metadata?.startDate;
      endDate = DatasetStore.metadata?.endDate;
    }
    const stations = StationStore.selectedStations.map((option) => option.id);

    if (!resId || !startDate || !endDate) {
      throw Error(
        'Resource, start date and end date are required for downloading station data'
      );
    }

    const startDateStr = formatDateForQuery(startDate);
    const endDateStr = formatDateForQuery(endDate);

    const pathWithArgs =
      await StationApiAxiosParamCreator().historicalStationDataStationHistoricalResourceIdGet(
        resId,
        [params.join(',')],
        startDateStr,
        endDateStr,
        [stations.join(',')],
        fileFormat,
        fileBasename
      );

    return ConfigStore.dataApiUrl + pathWithArgs.url;
  }

  async getGridDatasetDownloadURL(obj: {
    fileName: string;
    downloadRange: [Date, Date];
  }): Promise<string> {
    const fileBasename = obj.fileName.split('.')[0];
    const fileExtension = obj.fileName.split('.')[1];
    let fileFormat: OutputFormat.Geojson | OutputFormat.Netcdf | undefined;
    if (fileExtension === 'json') {
      fileFormat = OutputFormat.Geojson;
    } else if (fileExtension === 'cdf' || fileExtension === 'hdf') {
      fileFormat = OutputFormat.Netcdf;
    }
    const params = ParameterStore.selectedParameters.map(
      (option) => option.name
    );
    const resId = DatasetStore.metadata?.datasetParams.resourceId;
    let startDate;
    let endDate;
    const dateRange = obj.downloadRange;
    if (dateRange !== undefined) {
      startDate = dateRange[0];
      endDate = dateRange[1];
    } else {
      // fallback to full range available in dataset
      startDate = DatasetStore.metadata?.startDate;
      endDate = DatasetStore.metadata?.endDate;
    }
    const bbox = GridStore.bbox?.toString();

    if (!resId || !startDate || !endDate || !bbox) {
      throw Error(
        'Resource, start date, end date and bbox are required for downloading grid data'
      );
    }

    // format dates and include the whole day of the end date
    const startDateStr = formatDateForQuery(startDate);
    const endDateStr = formatDateForQuery(endDate);

    const pathWithArgs =
      await GridApiAxiosParamCreator().historicalGridDataGridHistoricalResourceIdGet(
        resId,
        params,
        startDateStr,
        endDateStr,
        bbox,
        fileFormat,
        fileBasename
      );

    return ConfigStore.dataApiUrl + pathWithArgs.url;
  }

  async getTimeseriesDatasetDownloadURL(obj: {
    fileName: string;
    downloadRange: [Date, Date];
  }): Promise<string> {
    const fileBasename = obj.fileName.split('.')[0];
    const fileExtension = obj.fileName.split('.')[1];
    let fileFormat: OutputFormat.Geojson | OutputFormat.Csv | undefined;
    if (fileExtension === 'json') {
      fileFormat = OutputFormat.Geojson;
    } else if (fileExtension === 'csv') {
      fileFormat = OutputFormat.Csv;
    }
    const params = ParameterStore.selectedParameters.map(
      (option) => option.name
    );
    const resId = DatasetStore.metadata?.datasetParams.resourceId;
    let startDate;
    let endDate;
    const dateRange = obj.downloadRange;
    if (dateRange !== undefined) {
      startDate = dateRange[0];
      endDate = dateRange[1];
    } else {
      // fallback to full range available in dataset
      startDate = DatasetStore.metadata?.startDate;
      endDate = DatasetStore.metadata?.endDate;
    }

    const coords = Object.values(TimeseriesStore.activeCoords).map(
      (coord) => `${coord.lat}, ${coord.lng}`
    );

    if (!resId || !startDate || !endDate || coords.length < 1) {
      throw Error(
        'Resource, start date, end date and lat/lon are required for downloading timeseries data'
      );
    }

    // format dates and include the whole day of the end date
    const startDateStr = formatDateForQuery(startDate);
    const endDateStr = formatDateForQuery(endDate);

    const pathWithArgs =
      await TimeseriesApiAxiosParamCreator().historicalTimeseriesTimeseriesHistoricalResourceIdGet(
        resId,
        params,
        startDateStr,
        endDateStr,
        coords,
        fileFormat,
        fileBasename
      );

    return ConfigStore.dataApiUrl + pathWithArgs.url;
  }
}

export default new DownloadService();
